import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { CACHE_TIMES } from './config';
import {
  fetchAnalyticsData,
  createAnalyticsUrl,
  serializeAnalyticsFilters,
  normalizeAnalyticsFilters,
  createDefaultBaseFilters,
} from './utils';
import { BASE_URLS } from './config';
import { buildAnalyticsQueryKey } from './useAnalyticsQuery';

const SESSION_ANALYTICS_CONFIG = {
  baseUrl: BASE_URLS.SESSIONS,
  queryKeyPrefix: 'analytics',
};

const WELLNESS_ANALYTICS_CONFIG = {
  baseUrl: BASE_URLS.WELLNESS,
  queryKeyPrefix: 'wellness',
};

export function usePrefetchAnalytics(filters: BaseAnalyticsFilter) {
  const queryClient = useQueryClient();

  const normalizedFilters = useMemo(
    () => normalizeAnalyticsFilters(filters, createDefaultBaseFilters()),
    [filters],
  );

  const filtersKey = useMemo(
    () => serializeAnalyticsFilters(normalizedFilters).toString(),
    [normalizedFilters],
  );

  useEffect(() => {
    const prefetchTimeout = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: buildAnalyticsQueryKey('/minutes', normalizedFilters, SESSION_ANALYTICS_CONFIG),
        queryFn: () =>
          fetchAnalyticsData(
            createAnalyticsUrl(SESSION_ANALYTICS_CONFIG.baseUrl, '/minutes', normalizedFilters),
          ),
        staleTime: CACHE_TIMES.STALE_TIME,
      });

      queryClient.prefetchQuery({
        queryKey: buildAnalyticsQueryKey('/metrics', normalizedFilters, WELLNESS_ANALYTICS_CONFIG),
        queryFn: () =>
          fetchAnalyticsData(
            createAnalyticsUrl(WELLNESS_ANALYTICS_CONFIG.baseUrl, '/metrics', normalizedFilters),
          ),
        staleTime: CACHE_TIMES.STALE_TIME,
      });
    }, CACHE_TIMES.PREFETCH_DELAY);

    return () => clearTimeout(prefetchTimeout);
  }, [filtersKey, normalizedFilters, queryClient]);
}
